import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useToast } from '../../../../hooks';
import proofkitFilemakerService from '../../../../services/proofkitFilemakerService';
import { ArrowLeft, Edit, Trash } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, Button, Badge, Label, RadioPills } from '../../../../components/ui';

const TEMPLATES_LAYOUT = process.env.REACT_APP_FILEMAKER_LAYOUT_TEMPLATES || "templates";

export default function TemplatePreview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();

  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState(null);
  const [templateRecordId, setTemplateRecordId] = useState(null);
  const [previewPreset, setPreviewPreset] = useState("desktop");

  const fetchTemplate = async () => {
    try {
      setLoading(true);
      setTemplate(null);
      setTemplateRecordId(null);

      let record = null;

      try {
        const res = await proofkitFilemakerService.getRecord(TEMPLATES_LAYOUT, id);
        record = res?.data || null;
      } catch (_err) {
        const findRes = await proofkitFilemakerService.findRecords(TEMPLATES_LAYOUT, {
          query: [{ ClavePrincipal: String(id) }],
          limit: 1,
          offset: 1,
        });
        record = Array.isArray(findRes?.data) ? findRes.data[0] : null;
      }

      setTemplate(record);
      setTemplateRecordId(record?.recordId || null);
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo cargar la plantilla",
        variant: "destructive",
      });
      setTemplate(null);
      setTemplateRecordId(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplate();
  }, [id]);

  const templateData = template?.fieldData;
  const templateHtml = templateData?.html || templateData?.contenido_html || templateData?.contenido || "";

  const previewPresets = {
    mobile: { label: "Móvil", width: 375, height: 600 },
    desktop: { label: "Desktop", width: 1024, height: 600 },
    full: { label: "Ancho completo", width: null, height: 600 },
  };

  const preset = previewPresets[previewPreset] || previewPresets.desktop;

  const getEstadoBadge = (estado) => {
    const n = Number(estado);
    if (!Number.isNaN(n)) {
      return n ? <Badge variant="default">Activo</Badge> : <Badge variant="secondary">Inactivo</Badge>;
    }

    const v = String(estado || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin estado</Badge>;
    if (v === "activo") return <Badge variant="default">Activo</Badge>;
    if (v === "inactivo") return <Badge variant="secondary">Inactivo</Badge>;
    if (v === "archivado") return <Badge variant="outline">Archivado</Badge>;
    return <Badge variant="secondary">{estado}</Badge>;
  };

  const getTipoBadge = (tipo) => {
    const v = String(tipo || "").toLowerCase();
    if (!v) return <Badge variant="secondary">Sin tipo</Badge>;
    if (v === "email") return <Badge variant="default">Email</Badge>;
    if (v === "sms") return <Badge variant="outline">SMS</Badge>;
    if (v === "notificacion") return <Badge variant="secondary">Notificación</Badge>;
    return <Badge variant="secondary">{tipo}</Badge>;
  };

  const handleDeleteTemplate = async () => {
    if (!window.confirm("¿Estás seguro de eliminar esta plantilla?")) {
      return;
    }

    try {
      await proofkitFilemakerService.deleteRecord(TEMPLATES_LAYOUT, templateRecordId || id);
      toast({
        title: "Plantilla eliminada",
        description: "La plantilla fue eliminada exitosamente",
      });
      navigate("/campanas/templates");
    } catch (err) {
      toast({
        title: "Error",
        description: "No se pudo eliminar la plantilla",
        variant: "destructive",
      });
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas/templates")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8">Cargando plantilla...</div>
      </div>
    );
  }

  if (!templateData) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Button variant="ghost" onClick={() => navigate("/campanas/templates")}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>
        <div className="text-center py-8 text-[var(--yool-error)]">Plantilla no encontrada</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={() => navigate("/campanas/templates")}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          Volver
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate(`/campanas/templates/${id}/edit`)}>
            <Edit className="h-4 w-4 mr-2" />
            Editar
          </Button>
          <Button variant="destructive" onClick={handleDeleteTemplate}>
            <Trash className="h-4 w-4 mr-2" />
            Eliminar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Estado</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getEstadoBadge(templateData.activo ?? templateData.estado)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Tipo</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{getTipoBadge(templateData.tipo)}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Fecha Creación</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{templateData?.["MarcaFechaYHoraCreación"] || templateData.fecha_creacion || "-"}</div>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Información de la Plantilla</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground">Nombre</Label>
              <p className="font-medium">{templateData.nombre || "Sin nombre"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Asunto</Label>
              <p className="font-medium">{templateData.subject || templateData.asunto || "-"}</p>
            </div>
            <div>
              <Label className="text-sm text-muted-foreground">Descripción</Label>
              <p className="font-medium">{templateData.descripcion || "-"}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Contenido de la Plantilla</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
            {/* Radio pill del Yool DS: un ancho entre tres, todos a la vista.
                Ver components/ui/switch.jsx. */}
            <RadioPills
              aria-label="Ancho de la vista previa"
              value={previewPreset}
              onChange={setPreviewPreset}
              options={[
                { value: "mobile", label: "Móvil" },
                { value: "desktop", label: "Desktop" },
                { value: "full", label: "Ancho completo" },
              ]}
            />
            <div className="text-sm text-muted-foreground">
              {preset.width ? `${preset.width}px` : "100%"}
            </div>
          </div>

          {templateHtml ? (
            <div className="rounded-md border bg-white overflow-hidden">
              <div style={{ width: preset.width ? `${preset.width}px` : "100%", maxWidth: "100%", margin: "0 auto" }}>
                <iframe
                  title="Vista previa de plantilla"
                  sandbox="allow-same-origin"
                  srcDoc={templateHtml}
                  className="w-full"
                  style={{ height: preset.height }}
                />
              </div>
            </div>
          ) : (
            <div className="text-sm text-muted-foreground">Sin contenido</div>
          )}

          {templateHtml && (
            <p className="text-sm text-muted-foreground mt-2">
              Puedes usar variables como: {"{{nombre}}"}, {"{{email}}"}, {"{{empresa}}"}, etc.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
